/**
 * Shared tenant branding + feature-config model for the Tenant SPA.
 *
 * SINGLE SOURCE OF TRUTH: everything here maps 1:1 onto
 * tis-auth-tenant-service's `tenant_configurations` row
 * (`tenantBrandingSchema` in tis-contracts for `branding`, and the named
 * `featureFlags` convention agreed in
 * docs/tis-runs/tenant-gap-closure/shared-branding-contract.md). The Tenant
 * SPA, Talent SPA and Hub SPA all read the SAME config — this file never
 * stores branding anywhere else. Image fields (logo/favicon/background)
 * are plain URLs once stored; the live-preview panel applies colors/fonts
 * to a SCOPED container only, never document.documentElement (the Tenant
 * admin chrome is platform-owned, see src/index.css's top comment).
 */

export const PLATFORM_DEFAULT = {
  shortName: "TIS",
  logoUrl: null,
  faviconUrl: null,
  backgroundImageUrl: null,
  colors: {
    primary: "201 84% 19%",
    primaryForeground: "210 40% 98%",
    secondary: "210 40% 96%",
    secondaryForeground: "222 47% 11%",
    accent: "31 100% 65%",
    accentForeground: "201 84% 15%",
    background: "220 20% 97%",
    foreground: "222 47% 11%",
    ring: "201 84% 19%",
  },
  fonts: {
    heading: "'Outfit', system-ui, sans-serif",
    sans: "'DM Sans', system-ui, sans-serif",
    mono: "'JetBrains Mono', monospace",
  },
};

/** Typography presets offered in the branding step (reference "Typography"
 * selector). Each maps to a {heading, sans} pair of already-loaded webfonts
 * (see src/index.css @import) so a selection renders with no extra load. */
export const FONT_PRESETS = [
  { id: "modern", label: "Inter (Modern)", heading: "'Outfit', system-ui, sans-serif", sans: "'DM Sans', system-ui, sans-serif" },
  { id: "classic", label: "Georgia (Classic)", heading: "Georgia, 'Times New Roman', serif", sans: "Georgia, 'Times New Roman', serif" },
  { id: "mono", label: "JetBrains (Technical)", heading: "'JetBrains Mono', monospace", sans: "'JetBrains Mono', monospace" },
];

/** Brand color swatches offered in the branding step — each is a full,
 * self-consistent token set (primary/secondary/accent) so a pick looks
 * coherent in the preview and in Talent/Hub. */
export const BRAND_SWATCHES = [
  { id: "blue", primary: "221 83% 53%", secondary: "210 40% 96%", accent: "214 95% 93%" },
  { id: "violet", primary: "262 83% 58%", secondary: "263 30% 95%", accent: "262 90% 95%" },
  { id: "emerald", primary: "162 72% 35%", secondary: "162 20% 94%", accent: "162 60% 92%" },
  { id: "orange", primary: "25 95% 53%", secondary: "25 30% 95%", accent: "25 90% 94%" },
  { id: "rose", primary: "340 82% 52%", secondary: "340 30% 96%", accent: "340 85% 95%" },
];

/** Named feature toggles (reference "Feature Toggles" grid). Keys are the
 * stable `featureFlags.modules.*` contract Talent/Hub read. */
export const FEATURE_MODULES = [
  { id: "candidatePipeline", label: "Candidate Pipeline", description: "Manage candidates and hiring pipeline." },
  { id: "vendorManagement", label: "Vendor Management", description: "Manage staffing vendors and suppliers." },
  { id: "marketing", label: "Marketing", description: "Campaigns and talent engagement." },
  { id: "analytics", label: "Analytics", description: "Insights and reporting dashboards." },
  { id: "sso", label: "SSO", description: "Enable single sign-on for your team." },
  { id: "notifications", label: "Notifications", description: "Email and in-app notifications." },
];

/** Per-portal navigation modules (reference "Navigation Modules" grid). */
export const PORTAL_NAV_MODULES = {
  talent: [
    { id: "home", label: "Home" },
    { id: "jobs", label: "Jobs / Opportunities" },
    { id: "applications", label: "Applications" },
    { id: "community", label: "Talent Community" },
    { id: "analytics", label: "Analytics" },
    { id: "administration", label: "Administration" },
  ],
  hub: [
    { id: "home", label: "Home" },
    { id: "people", label: "People" },
    { id: "operations", label: "Operations" },
    { id: "analytics", label: "Analytics" },
    { id: "administration", label: "Administration" },
  ],
  serviqo: [
    { id: "home", label: "Home" },
    { id: "cases", label: "Cases" },
    { id: "contacts", label: "Contacts" },
    { id: "sla", label: "SLA" },
    { id: "analytics", label: "Analytics" },
    { id: "administration", label: "Administration" },
  ],
};

export function defaultFeatureConfig() {
  return {
    modules: {
      candidatePipeline: true,
      vendorManagement: true,
      marketing: false,
      analytics: true,
      sso: false,
      notifications: true,
    },
    portals: {
      talent: {
        displayName: "",
        tagline: "",
        subdomain: "",
        nav: { home: true, jobs: true, applications: true, community: true, analytics: false, administration: true },
      },
      hub: {
        displayName: "",
        tagline: "",
        subdomain: "",
        nav: { home: true, people: true, operations: true, analytics: true, administration: true },
      },
      serviqo: {
        displayName: "",
        tagline: "",
        subdomain: "",
        nav: { home: true, cases: true, contacts: true, sla: true, analytics: false, administration: true },
      },
    },
    labels: {},
    locale: { defaultLanguage: "en-US", timezone: "America/New_York" },
  };
}

/** Merge a possibly-partial stored branding object over the platform
 * default so the preview always has a full, renderable token set. */
export function buildPreviewTheme(branding) {
  const b = branding || {};
  return {
    shortName: b.shortName || PLATFORM_DEFAULT.shortName,
    logoUrl: b.logoUrl || PLATFORM_DEFAULT.logoUrl,
    faviconUrl: b.faviconUrl || PLATFORM_DEFAULT.faviconUrl,
    backgroundImageUrl: b.backgroundImageUrl || PLATFORM_DEFAULT.backgroundImageUrl,
    colors: { ...PLATFORM_DEFAULT.colors, ...(b.colors || {}) },
    fonts: { ...PLATFORM_DEFAULT.fonts, ...(b.fonts || {}) },
  };
}

/**
 * Resolve the effective theme for ONE portal. The shared brand (buildPreviewTheme)
 * is the base; a portal may override any subset of color tokens via
 * `portalColors` (featureConfig.portals.<id>.colors). An unset override
 * key falls through to the shared brand, so "default" portals look
 * identical and a tenant can diverge Talent from Hub only where they
 * choose to. Logo/fonts/background stay shared (brand-level), only colors
 * are per-portal.
 */
export function buildPortalTheme(branding, portalColors) {
  const base = buildPreviewTheme(branding);
  if (!portalColors || Object.keys(portalColors).length === 0) return base;
  // Drop empty-string overrides so a cleared field falls back to the brand.
  const overrides = Object.fromEntries(
    Object.entries(portalColors).filter(([, v]) => typeof v === "string" && v.trim() !== ""),
  );
  return { ...base, colors: { ...base.colors, ...overrides } };
}

/** Accept a `#rrggbb` hex OR an `H S% L%` triple and return the
 * `H S% L%` form the CSS variables and tenantBrandingSchema expect.
 * Returns null for an unparseable value (caller shows a validation error). */
export function toHslTriple(value) {
  if (!value) return null;
  const trimmed = value.trim();
  // Already an HSL triple like "221 83% 53%"
  if (/^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/.test(trimmed)) return trimmed;
  // Hex -> HSL
  const hex = trimmed.replace("#", "");
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return null;
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const bl = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, bl);
  const min = Math.min(r, g, bl);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - bl) / d + (g < bl ? 6 : 0);
    else if (max === g) h = (bl - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** HSL triple -> #rrggbb, for native <input type=color> round-tripping. */
export function hslTripleToHex(triple) {
  if (!triple) return "#000000";
  const m = /^(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%$/.exec(triple.trim());
  if (!m) return "#000000";
  const h = Number(m[1]) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}


// --- Derive a brand palette from a tenant's website -----------------------

/** Normalize a user-typed domain/URL to a bare hostname (acme.com). */
export function normalizeDomain(input) {
  if (!input) return null;
  let s = input.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/.*$/, "");
  // Must look like a domain (has a dot, no spaces).
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s)) return null;
  return s;
}

/** RGB -> "H S% L%" triple. */
/**
 * Build a full, coherent branding palette from one dominant brand color
 * (as {h,s,l}). Primary is the brand color; secondary/accent are tints of
 * the same hue; background near-white, foreground near-black. Keeps the
 * tenant's hue identity while guaranteeing readable, self-consistent
 * tokens — the same shape BRAND_SWATCHES uses.
 */
export function paletteFromHsl({ h, s, l }) {
  // Clamp primary lightness into a usable band so a near-white/near-black
  // favicon pixel doesn't produce an unusable primary.
  const pl = Math.min(60, Math.max(35, l));
  const ps = Math.min(90, Math.max(45, s));
  return {
    primary: `${h} ${ps}% ${pl}%`,
    primaryForeground: pl > 55 ? "222 47% 11%" : "210 40% 98%",
    secondary: `${h} 30% 96%`,
    secondaryForeground: "222 47% 11%",
    accent: `${h} 85% 94%`,
    accentForeground: `${h} ${ps}% ${Math.max(25, pl - 15)}%`,
    background: "0 0% 100%",
    foreground: "222 47% 11%",
    ring: `${h} ${ps}% ${pl}%`,
  };
}

/**
 * Extract a dominant brand color from a website's favicon and return a
 * full palette (via paletteFromHsl). Uses Google's favicon service, which
 * serves the icon with permissive CORS so the pixels can be read from a
 * canvas — no backend and no cross-origin fetch of the tenant's own site
 * (which would be CORS-blocked). Resolves null on any failure (no favicon,
 * blocked, transparent icon) so the caller can show "couldn't read".
 *
 * This is a best-effort convenience to pre-fill the palette; the admin can
 * always override every token by hand afterwards.
 */
export function paletteFromWebsite(domain, { timeoutMs = 8000 } = {}) {
  const host = normalizeDomain(domain);
  if (!host) return Promise.resolve(null);
  // Server-side extraction (tis-tenant-platform-service POST
  // /branding/extract). The browser cannot do this reliably: loading a
  // site's favicon into a canvas to read its pixels is defeated by CORS on
  // almost every real site (the favicon services and brand sites send no
  // Access-Control-Allow-Origin, so the canvas taints and the read throws),
  // which left signup falling back to default colors. The backend fetches
  // the site itself (no CORS), pulls the brand color from theme-color meta
  // / manifest / dominant inline CSS, and returns a full palette + the
  // site's real logo. SSRF-guarded server-side (public hosts only).
  const base = process.env.REACT_APP_TENANT_API_URL;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(`${base}/api/v1/branding/extract`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ domain: host }),
    signal: controller.signal,
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data || !data.colors) return null;
      // Preserve the shape callers expect ({ palette, faviconUrl,
      // primaryHsl }); map the endpoint's logo_url -> faviconUrl.
      return { palette: data.colors, faviconUrl: data.logo_url || null, primaryHsl: data.colors.primary, source: data.source };
    })
    .catch(() => null) // network/timeout => caller keeps current theme
    .finally(() => clearTimeout(timer));
}


// --- Portal host scheme ----------------------------------------------------

/**
 * The portals are served under transcendit.io:
 *   dev:  app.talent.dev.transcendit.io / app.hub.dev.transcendit.io
 *   prod: app.talent.transcendit.io     / app.hub.transcendit.io
 * The base domain is env-driven so dev and prod differ without code change.
 * Tenant-specific custom domains are a future concern; for now every tenant
 * is reached under the shared portal host.
 */
export const PORTAL_BASE_DOMAIN =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_PORTAL_BASE_DOMAIN) || "dev.transcendit.io";

/** Full portal URL for a portal id, e.g. portalUrl('talent') ->
 * https://app.talent.dev.transcendit.io */
export function portalUrl(portal, base = PORTAL_BASE_DOMAIN) {
  return `https://app.${portal}.${base}`;
}

/** Host (no scheme) for display in the preview browser bar. */
export function portalHost(portal, base = PORTAL_BASE_DOMAIN) {
  return `app.${portal}.${base}`;
}
